import {useCallback, useEffect, useRef, useState} from 'react';
import lazyLoader from '../utils/http/lazy-loader';
import {apiClient} from '../http/query-client';
import {RecaptchaAction} from '../core/settings/settings';
import {toast} from '../ui/toast/toast';
import {message} from '../i18n/message';
import {useSettings} from '../core/settings/use-settings';
import {useSelectedLocale} from '../i18n/selected-locale';

const SCRIPT_URL = 'https://smartcaptcha.cloud.yandex.ru/captcha.js';
const SUPPORTED_HL = ['ru', 'en', 'be', 'kk', 'tt', 'uk', 'uz', 'tr'] as const;

function widgetLanguage(localeCode: string): (typeof SUPPORTED_HL)[number] {
  const lang = localeCode.split(/[-_]/)[0]?.toLowerCase() || 'en';
  return (SUPPORTED_HL as readonly string[]).includes(lang)
    ? (lang as (typeof SUPPORTED_HL)[number])
    : 'en';
}

export function useSmartCaptcha(action: RecaptchaAction) {
  const {smartcaptcha: {client_key, enable} = {}} = useSettings();
  const enabled = Boolean(client_key && enable?.[action]);
  const {localeCode} = useSelectedLocale();
  const hl = widgetLanguage(localeCode);

  const [isVerifying, setIsVerifying] = useState(false);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const tokenRef = useRef<string | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);

  const destroyWidget = useCallback(() => {
    if (widgetIdRef.current != null) {
      try {
        window.smartCaptcha?.destroy?.(widgetIdRef.current);
      } catch {
        // Widget may already be gone.
      }
      widgetIdRef.current = null;
    }
    tokenRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled || !client_key || !container) {
      destroyWidget();
      return;
    }

    let cancelled = false;

    (async () => {
      await lazyLoader.loadAsset(SCRIPT_URL, {id: 'smartcaptcha-js'});
      const ready = await waitForSmartCaptcha();
      if (cancelled || !ready || !window.smartCaptcha) {
        return;
      }

      destroyWidget();
      widgetIdRef.current = window.smartCaptcha.render(container, {
        sitekey: client_key,
        invisible: false,
        test: false,
        hideShield: false,
        hl,
        callback: token => {
          tokenRef.current = token || null;
        },
      });
    })();

    return () => {
      cancelled = true;
      destroyWidget();
    };
  }, [enabled, client_key, hl, container, destroyWidget]);

  const verify = useCallback(async () => {
    if (!enabled) {
      return true;
    }

    setIsVerifying(true);
    try {
      const widgetId = widgetIdRef.current;
      if (widgetId == null || !window.smartCaptcha) {
        toast.danger(message('Could not verify you are human.'));
        return false;
      }

      const token =
        tokenRef.current || window.smartCaptcha.getResponse?.(widgetId) || null;

      if (!token) {
        toast.danger(message('Could not verify you are human.'));
        return false;
      }

      const isValid = await apiClient
        .post('smartcaptcha/verify', {token})
        .then(r => Boolean(r.data.success))
        .catch(() => false);

      if (!isValid) {
        toast.danger(message('Could not verify you are human.'));
      }

      tokenRef.current = null;
      try {
        window.smartCaptcha.reset?.(widgetId);
      } catch {
        // Token is one-time; reset for the next submit.
      }

      return isValid;
    } finally {
      setIsVerifying(false);
    }
  }, [enabled]);

  return {enabled, verify, isVerifying, containerRef};
}

async function waitForSmartCaptcha(timeoutMs = 10000): Promise<boolean> {
  if (window.smartCaptcha) {
    return true;
  }

  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    await new Promise(resolve => setTimeout(resolve, 50));
    if (window.smartCaptcha) {
      return true;
    }
  }

  return false;
}
